/**
 * columnHeaderWidgets —— 列头渲染挂件。
 *
 * 【为何单独成文件】这些是「列头怎么画」而不是「列怎么翻译」：列翻译（`buildBedrockColumns`）
 * 决定有哪些列，本文件决定列头长什么样。拆开后两者文件名都诚实
 * ——改「批量编辑弹层确定按钮禁用」可直接定位到这里。
 */
import React, { useState } from 'react';
import { Button, Input, Popover, Radio, Select } from '@bedrock/components';
import { BatchEdit } from '@bedrock/icons-react';
import { localPx2rem } from '@mpc/common/src/utils';
import { CELL_CONTROL_WIDTH_STYLE, CELL_SELECT_DROPDOWN_PROPS, MIN_SEARCHABLE_OPTION_COUNT } from './cellControlStyle';
import type { BatchEditMeta, ColumnFixedButton } from './types';

/**
 * 列头「批量编辑」入口的字号 / 图标尺寸（设计值 px，经 localPx2rem 换算为 rem 以随根字号缩放）。
 *
 * ⚠️ 不能靠 Button 的 `size` 档位表达：`size="xSmall"` 的字号由组件内部决定，
 * 与设计稿要求的文字 14px / 图标 16px 不一致。故显式下发字号，
 * 图标用 icons-react 的 `size`（与 dragCell 的把手图标同一写法）。
 */
const BATCH_EDIT_TEXT_FONT_SIZE = 14;
const BATCH_EDIT_ICON_SIZE = 16;

/**
 * 列头固定按钮层：把 `fixedButton` 的 left / right 固定在列头角落。
 *
 * 【为何不用「标题容器内 absolute」】实测 th 的层级是
 *   `TH.rock-table-cell(position:relative; padding:11px 13.2px)`
 *     `> DIV.rock-table-th-wrapper(position:static; inline-flex)`
 *       `> 标题容器（常自带宽高收缩的 relative）`
 * 若把按钮放进标题容器内用 absolute，有两个问题：
 *  ① 它相对**内容区**定位（实测偏移量恰好等于 th 的 padding），拿不到列头的物理角落；
 *  ② 该容器是 inline-flex、宽度收缩到标题内容，标题一宽一窄按钮就跟着漂；
 *  且常需要用 margin 给脱离文档流的按钮「预留」位置，标题一换行即错位。
 *
 * 【本方案】把按钮层与标题**并列**渲染为 th 的内容。此时按钮层最近的 positioned 祖先是
 * th 自身（中间的 th-wrapper 是 static），absolute 的包含块即 th 的 **padding box**，
 * 于是 `inset-0` + `top-0/left-0` 恰好落在列头的 padding 外沿 ——
 * 即「忽略列头 padding 的物理左上/右上角」，且零硬编码、完全不受标题宽度与换行影响。
 *
 * 【指针】外层 `pointer-events-none` 避免整层盖住标题的点击与 tooltip，内层 `auto` 恢复按钮可点。
 *
 * 【为何内层要 `flex`】按钮内容若是 `inline-flex`/`span` 等内联级元素，会参与**行盒**布局，
 * 其 bbox 受 line-height 影响产生偏移（实测：line-height 18px + font-size 12px → 顶部偏移 3px）。
 * 把 wrapper 设为 flex 容器后，内容是 flex item、不再参与行盒对齐，bbox 精确贴合角落。
 */
export const ColumnFixedButtonLayer: React.FC<{ btn?: ColumnFixedButton }> = ({ btn }) => {
  if (!btn?.left && !btn?.right) return null;
  return (
    <div className="absolute inset-0 pointer-events-none">
      {btn.left && <div className="absolute top-0 left-0 flex pointer-events-auto">{btn.left}</div>}
      {btn.right && <div className="absolute top-0 right-0 flex pointer-events-auto">{btn.right}</div>}
    </div>
  );
};

/**
 * 列标题容器（表头批量编辑 Popover：保留/覆盖 + 按列类型渲染，前置 BatchEdit 图标）：
 * 可承载「钉在左侧」等标题旁挂件（`extra`）与「批量编辑」入口（`meta`）—— **两者互相独立**。
 *
 * ⚠️ `meta` 未提供时仍会渲染 `extra` 与标题，只是没有批量编辑入口。
 * 这保证「某列不提供批量编辑」不会连带丢掉它的 `titleExtra`（曾因本组件把 `extra` 挂在
 * `meta` 分支下，导致级联列去掉 batchEdit 后「钉在左侧」按钮一并消失）。
 */
export const BatchEditCellTitle: React.FC<{
  title: string;
  /** 该列字段 key（批量写回回调的入参） */
  field: string;
  /** 批量编辑 meta；省略 = 该列不提供批量编辑入口 */
  meta?: BatchEditMeta;
  /** 标题旁的额外节点（如「钉在左侧」Pushpin），独立于 meta 渲染 */
  extra?: React.ReactNode;
  requiredMark?: boolean;
  onBatchApply: (field: string, value: string, mode: 'keep' | 'cover') => void;
}> = ({ title, field, meta, extra, onBatchApply, requiredMark }) => {
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<'keep' | 'cover'>('cover');
  const disabled = !value;
  const apply = () => { onBatchApply(field, value, mode); setVisible(false); setValue(undefined); };
  return (
    <span className="relative inline-flex flex-col items-start gap-1">
      {extra}
      <span className="whitespace-nowrap mt-4">{meta?.required && <span className="text-[#E55140]">*</span>}{title}</span>
      {/*
        ⚠️ 不要给 Popover 写死 zIndex —— baseUI 的浮层层级是**递增分配**的（后打开的浮层更高），
        写死一个大值（如 9999）会破坏该机制：popover 内再打开的下拉/日期/TextAreaPopover 只拿到
        约 1066 的常规值，反而**被外层 popover 盖住**（实测：批量编辑的 Select 下拉被本 popover 遮挡）。
        交给 baseUI 管理，嵌套浮层即自动保持正确层级。
      */}
      {meta && (
      <Popover visible={visible} onVisibleChange={setVisible} trigger="click" placement="bottomLeft" destroyTooltipOnHide
        content={
          <div className="w-[240px]">
            {/* 两处均为同级字段说明标签，字号须保持一致 */}
            <div className="text-base text-[var(--text-color-2)] mb-2">有内容时是否批量覆盖</div>
            <Radio.Group size="small" value={mode} onChange={(e) => setMode(e.target.value)} options={[{ name: '保留原值', value: 'keep' }, { name: '覆盖原值', value: 'cover' }]} />
            <div className="text-base text-[var(--text-color-2)] my-2">批量编辑/列（仅修改当前页）</div>
            {meta.render ? (
              // 自定义填值控件：业务传入，与列内单元格控件保持一致（引擎只取值消费）
              meta.render(value, setValue)
            ) : meta.type === 'select' ? (
              /* 引擎通用下拉按选项数收窄搜索（覆盖须写在展开之后） */
              <Select size="small" style={CELL_CONTROL_WIDTH_STYLE} options={meta.options} placeholder="请选择" value={value} onChange={(v) => setValue(v as string)} allowClear {...CELL_SELECT_DROPDOWN_PROPS} showSearch={(meta.options?.length ?? 0) > MIN_SEARCHABLE_OPTION_COUNT} />
            ) : (
              <Input size="small" style={CELL_CONTROL_WIDTH_STYLE} placeholder="请输入" value={value ?? ''} onChange={(e) => setValue(e.target.value)} />
            )}
            <div className="flex justify-end gap-2 mt-3">
              <Button size="small" type="checked-neutral" onClick={() => { setVisible(false); setValue(undefined); }}>取消</Button>
              <Button size="small" type="primary" disabled={disabled} onClick={apply}>确定</Button>
            </div>
          </div>
        }
      >
        <Button
          type="link"
          size="xSmall"
          className="!p-0 whitespace-nowrap"
          style={{ fontSize: localPx2rem(BATCH_EDIT_TEXT_FONT_SIZE) }}
          icon={<BatchEdit size={BATCH_EDIT_ICON_SIZE} />}
        >
          批量编辑
        </Button>
      </Popover>
      )}
    </span>
  );
};

/** 组标题（spec.required 时加红星；非必填组如「期望权益」不显示） */
export const groupTitle = (text: string, required?: boolean) => (
  <span className="font-semibold">{required && <span className="text-[#E55140]">*</span>}{text}</span>
);
/** 子列标题（必填子列加红星） */
export const subTitle = (text: string, required?: boolean) => (
  <>{required && <span className="text-[#E55140]">*</span>}{text}</>
);
